import { Component, inject, signal } from "@angular/core"
import { NgClass } from "@angular/common"
import { Router } from "@angular/router"
import { firstValueFrom } from "rxjs"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { FormField, form, submit } from "@angular/forms/signals"
import { CategoryTypes, CreatePerk, CreatePerkFactory, PERK_FIELD_LABELS } from "../../models"
import { PerksService, ToastService } from "../../services"
import { TableComponent } from "../../components/table/table.component"
import { FiltersComponent } from "../../components/filters/filters.component"
import { DateRangePickerComponent } from "../../components/date-range-picker/date-range-picker.component"
import { FiltersValue, emptyFilters, toDependencies } from "../../helpers/DependenciesForm"
import { invalidFieldsDetail, toServerFieldErrors } from "../../helpers/FormErrors"
import { BooleanInputComponent } from "../../components/boolean-input/boolean-input.component"
import { FileInputComponent } from "../../components/file-input/file-input.component"
import {
    CategoriesInputComponent,
    DictionaryInputComponent,
    LocationsInputComponent,
} from "../../components"

@Component({
    selector: "valben-perks-add",
    templateUrl: "./perks-add.page.html",
    styles: [`
        :host {
            width: 100%;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [
        NgClass,
        MatProgressSpinnerModule,
        FormField,
        FiltersComponent,
        DictionaryInputComponent,
        DateRangePickerComponent,
        BooleanInputComponent,
        FileInputComponent,
        CategoriesInputComponent,
        LocationsInputComponent,
    ],
})
export class PerksAddPage {
    private _perksService = inject(PerksService)
    private _messageService = inject(ToastService)
    private _router = inject(Router)

    categoriesType = CategoryTypes.Perk

    saving = signal(false)
    tomorrow = new Date(new Date().setHours(24, 0, 0, 0))

    model = signal<CreatePerk>(CreatePerkFactory.create())
    perkForm = form(this.model)

    filters = signal<FiltersValue>(emptyFilters())

    save(): void {
        this._create(false)
    }

    draft(): void {
        this._create(true)
    }

    back(): void {
        this._router.navigate(["/perks"])
    }

    private async _create(isDraft: boolean): Promise<void> {
        this.perkForm.isDraft().value.set(isDraft)
        this.perkForm.dependencies().value.set(toDependencies(this.filters()))
        this.saving.set(true)
        let failed = false

        const created = await submit(this.perkForm, {
            action: async f => {
                try {
                    await firstValueFrom(this._perksService.Create(f().value()))
                    return null
                } catch (err) {
                    failed = true
                    const { detail, errors } = toServerFieldErrors(err, this.perkForm, PERK_FIELD_LABELS)
                    this._error(detail)
                    return errors
                }
            },
            onInvalid: () => this._error(invalidFieldsDetail(this.perkForm, PERK_FIELD_LABELS)),
        })

        this.saving.set(false)
        if (failed || !created) return

        this._messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Uspješno uneseno",
            life: 10000,
        })
        TableComponent.resetPageOffset()
        this.back()
    }

    private _error(detail: string): void {
        this._messageService.add({ severity: "error", summary: "Error", detail, life: 10000 })
    }
}
